import React from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAnalytics } from 'reactfire'

import CompleteQuizLoader from '../Quiz/components/CompleteQuizLoader/CompleteQuizLoader'

import Header from './components/Header/Header'
import {
  ContentWrap,
  Heading,
  OrderedList,
  ListItem,
  ListItemInner,
  NameWrap,
  GetMoreWrap,
  SecondaryButton,
} from './styled'
import { MatchPercentValue } from './components/MatchPercentValue/MatchPercentValue'

import { QuizMatches } from 'src/api/quizMatches/types'
import { getQuizMatches } from 'src/api/quizMatches/quizMatches'
import { LoadingScreen } from 'src/components/Loading'
import { PhotoProgressBar } from 'src/components/PhotoProgressBar/PhotoProgressBar'
import { LEADER_BOARD_DETAIL } from 'src/constants/routes'
import { getQuiz } from 'src/api/quizzes/quizzes'
import { IQuiz } from 'src/api/quizzes/types'
import { QuizIcon } from 'src/components/QuizIcon/QuizIcon'
import { analyticsEvents } from 'src/constants/analytics/events'

export const QuizResults: React.FC = () => {
  const { id } = useParams()
  const { logEvent } = useAnalytics()

  const [isLoading, setIsLoading] = React.useState(true)
  const [quizMatches, setQuizMatches] = React.useState<QuizMatches>()
  const [quiz, setQuiz] = React.useState<IQuiz>()

  React.useEffect(() => {
    const fetchQuizMatches = async () => {
      try {
        const [quizMatchesData, quizData] = await Promise.all([
          getQuizMatches(id),
          getQuiz(id),
        ])
        setQuizMatches(quizMatchesData)
        setQuiz(quizData)
      } catch (e) {
        // notify user something went wrong
        console.error(e)
      }
      setIsLoading(false)
    }
    fetchQuizMatches()
    logEvent(analyticsEvents.quizResultsDisplayed)
  }, [id, logEvent])

  const handleGetMore = React.useCallback(async () => {
    if (quizMatches) {
      const nextPage = {
        skip: quizMatches.data.length,
      }
      try {
        const nextPageQuizMatches = await getQuizMatches(id, nextPage)

        setQuizMatches({
          ...nextPageQuizMatches,
          data: quizMatches.data.concat(nextPageQuizMatches.data),
        })
      } catch (e) {
        console.error('unhandled error', e)
      }
    }
  }, [id, quizMatches])

  const handleCalculateQuizResults = (calculatedQuizMatches: QuizMatches) => {
    setQuizMatches(calculatedQuizMatches)
  }

  if (isLoading) {
    return <LoadingScreen />
  }
  if (quizMatches && quiz && quizMatches.data.length === 0) {
    return (
      <CompleteQuizLoader
        bgEnd={quiz.colors.bgEnd}
        bgStart={quiz.colors.bgStart}
        onMatchesCalculated={handleCalculateQuizResults}
        quizId={quiz.id}
      />
    )
  }
  return (
    <>
      <Header />
      <ContentWrap>
        {quiz && (
          <QuizIcon imgSrc={quiz?.imageUrl} color={quiz?.colors.bgStart} />
        )}
        <Heading>{quiz?.title}</Heading>
        <OrderedList>
          {quizMatches?.data.map(
            ({ otherUser, connectionId, matchPercent }) => (
              <ListItem key={connectionId}>
                <Link
                  to={`${LEADER_BOARD_DETAIL.replace(
                    ':id',
                    String(connectionId)
                  )}?quizId=${id}`}
                >
                  <ListItemInner>
                    <div>
                      <PhotoProgressBar
                        uniqueIdentifier={connectionId}
                        progress={matchPercent}
                        imgUrl={otherUser.profilePicture}
                      />
                      <NameWrap>{otherUser.firstName}</NameWrap>
                    </div>
                    <MatchPercentValue matchPercent={matchPercent} />
                  </ListItemInner>
                </Link>
              </ListItem>
            )
          )}
          {quizMatches && quizMatches.data.length < quizMatches.total && (
            <GetMoreWrap>
              <SecondaryButton onClick={handleGetMore}>
                Load more
              </SecondaryButton>
            </GetMoreWrap>
          )}
        </OrderedList>
      </ContentWrap>
    </>
  )
}
